import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Filler,
  Tooltip,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import { format, parseISO } from 'date-fns';
import DashboardCard from './DashboardCard';
import { AXIS_TICKS, GRID_COLOUR, TOOLTIP } from './chartTheme';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Filler,
  Tooltip
);

// Active learners (a level) and signups (spiky counts) differ in scale and
// shape, so they get a chart each instead of sharing two y-axes.
function WeeklyActivity({ weeks }) {
  const labels = weeks.map((week) => format(parseISO(week.week_end), 'd MMM'));

  const weekTitle = (items) => {
    const week = weeks[items[0].dataIndex];
    return `${format(parseISO(week.week_start), 'd MMM')} – ${format(
      parseISO(week.week_end),
      'd MMM yyyy'
    )}`;
  };

  const baseOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    scales: {
      x: {
        grid: { display: false },
        ticks: { ...AXIS_TICKS, maxRotation: 0, autoSkipPadding: 12 },
      },
      y: {
        beginAtZero: true,
        grid: { color: GRID_COLOUR, drawBorder: false },
        ticks: { ...AXIS_TICKS, maxTicksLimit: 5 },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: { ...TOOLTIP, callbacks: { title: weekTitle } },
    },
  };

  const latest = weeks[weeks.length - 1];

  return (
    <DashboardCard
      title="Weekly activity"
      subtitle="Last 12 rolling 7-day windows, ending on each date. Covers all students, not only live cohorts."
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <div className="flex items-baseline justify-between mb-2">
            <p className="text-sm text-gray-700">Active learners</p>
            <p className="text-xs text-gray-500">
              This week{' '}
              <span className="font-semibold text-gray-900 tabular-nums">
                {latest.active_learners.toLocaleString()}
              </span>
            </p>
          </div>
          <div className="h-52">
            <Line
              options={{
                ...baseOptions,
                elements: {
                  line: { tension: 0.3, borderWidth: 2 },
                  point: { radius: 0, hoverRadius: 4 },
                },
              }}
              data={{
                labels,
                datasets: [
                  {
                    label: 'Active learners',
                    data: weeks.map((week) => week.active_learners),
                    borderColor: '#00999E',
                    backgroundColor: 'rgba(0, 153, 158, 0.08)',
                    pointBackgroundColor: '#00999E',
                    fill: true,
                  },
                ],
              }}
            />
          </div>
        </div>
        <div>
          <div className="flex items-baseline justify-between mb-2">
            <p className="text-sm text-gray-700">New signups</p>
            <p className="text-xs text-gray-500">
              This week{' '}
              <span className="font-semibold text-gray-900 tabular-nums">
                {latest.new_signups.toLocaleString()}
              </span>
            </p>
          </div>
          <div className="h-52">
            <Bar
              options={baseOptions}
              data={{
                labels,
                datasets: [
                  {
                    label: 'New signups',
                    data: weeks.map((week) => week.new_signups),
                    backgroundColor: '#8B173B',
                    borderRadius: 3,
                    maxBarThickness: 22,
                  },
                ],
              }}
            />
          </div>
        </div>
      </div>
    </DashboardCard>
  );
}

export default WeeklyActivity;
